import React, { Component, ErrorInfo, ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  autoReloadSeconds?: number // Auto-reload after this many seconds (for kiosk mode)
  darkMode?: boolean // Use dark theme (for kiosk)
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  countdown: number
}

export class ErrorBoundary extends Component<Props, State> {
  private countdownInterval: NodeJS.Timeout | null = null

  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      countdown: props.autoReloadSeconds || 30
    }
  }

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorInfo: null
    }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo)
    this.setState({
      error,
      errorInfo
    })

    // Start auto-reload countdown for kiosk mode
    if (this.props.autoReloadSeconds) {
      this.startCountdown()
    }
  }

  public componentWillUnmount() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
  }

  private startCountdown = () => {
    this.countdownInterval = setInterval(() => {
      this.setState(prev => {
        if (prev.countdown <= 1) {
          this.handleReset()
          return prev
        }
        return { countdown: prev.countdown - 1 }
      })
    }, 1000)
  }

  private handleReset = () => {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      countdown: this.props.autoReloadSeconds || 30
    })
    window.location.reload()
  }

  private handleGoHome = () => {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
    window.location.href = '/'
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      const isDark = this.props.darkMode

      return (
        <div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
          <div className={`max-w-md w-full rounded-lg shadow-lg p-6 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
            <div className={`flex items-center justify-center w-12 h-12 mx-auto rounded-full mb-4 ${isDark ? 'bg-red-900/50' : 'bg-red-100'}`}>
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>

            <h2 className={`text-xl font-semibold text-center mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Something went wrong
            </h2>

            <p className={`text-sm text-center mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              We encountered an unexpected error. The issue has been logged.
            </p>

            {this.props.autoReloadSeconds && (
              <p className={`text-sm text-center mb-4 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                Auto-reloading in {this.state.countdown} seconds...
              </p>
            )}

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className={`mb-6 p-3 rounded text-xs font-mono overflow-auto max-h-40 ${isDark ? 'bg-gray-900 text-gray-400' : 'bg-gray-100 text-gray-700'}`}>
                <p className="font-semibold mb-1">Error:</p>
                <p>{this.state.error.toString()}</p>
                {this.state.errorInfo && (
                  <>
                    <p className="font-semibold mt-2 mb-1">Component Stack:</p>
                    <pre className="whitespace-pre-wrap text-xs">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={this.handleReset}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Reload
              </button>

              <button
                onClick={this.handleGoHome}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${isDark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
              >
                <Home className="w-4 h-4" />
                Go Home
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary